import React, { useState, useEffect, useCallback } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { useEmployerJobApi } from "../../../_common/hooks/actions/job/appEmployerJobApiHook";
import BulkResumeUploadModal from "./BulkResumeUploadModal";
import ViewProfileModal from "../_common/ViewProfileModal";
import SearchBox from "../_common/SearchBox";
import ProgressIndicator from "../_common/ProgressIndicator";
import { CANDIDATE_STATUS, REFRESH_INTERVAL } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useToaster } from "../../../_common/hooks/actions/common/appToasterHook";
import UpgradePlanModal from "../../modal/UpgradePlanModal";
import { useAppUserDataSelector, useAppUserDetailsSelector } from "../../../_common/hooks/selectors/userSelector";

interface TalentScoutingProps {
  jobId?: string;
  currentJob?: any; // Job details passed from parent
}

interface Candidate {
  id: string;
  jobseekerId?: number;
  name: string;
  avatar: string;
  position: string;
  status: string;
  type: number; // Store raw type number instead of translated string
  user_type?: string; // User type: "bulk-cv" for bulk uploads
  get_new_data?: number; // Flag to indicate if this is a new candidate
  location?: string;
  experience?: string;
  gender?: string;
  salary?: string;
  degree?: string;
  photo?: string;
  candidateDetails?: any; // Store full candidate details for ViewProfileModal
  currentDesignation?: any;
}

export default function TalentScouting({ jobId, currentJob }: TalentScoutingProps) {
  const { t } = useTranslation("job-details");
  const toast = useToaster();
  const userData = useAppUserDataSelector();
  const userDetails = useAppUserDetailsSelector();
  console.log("userDetails", userDetails);

  // Check if user is on free plan
  const isFreePlan = userData?.subscription?.price === 0;

  const [currentPage, setCurrentPage] = useState(1);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Separate state for refresh operations
  const [extraValueAvailable, setExtraValueAvailable] = useState(false); // State for extra value availability
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // State for upgrade plan modal
  const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(false); // State for first-time load
  const [firstTimeLoadComplete, setFirstTimeLoadComplete] = useState(false); // State to track if first-time load is complete
  const [savedRefreshTime, setSavedRefreshTime] = useState<number | null>(null); // Store the saved refresh time from API
  const [isTogglingRefresh, setIsTogglingRefresh] = useState(false); // State to prevent multiple toggle clicks
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number | null>(null); // null = All
  const [showNoNewCandidatesAlert, setShowNoNewCandidatesAlert] = useState(false); // State for no new candidates alert
  const [showCooldownAlert, setShowCooldownAlert] = useState(false); // State for cooldown period alert
  const [cooldownAlertMessage, setCooldownAlertMessage] = useState(""); // Store cooldown alert message

  // Pagination constants
  const ITEMS_PER_PAGE = 10;

  // Hook for API calls
  const jobApi = useEmployerJobApi();

  // Helper function to calculate total pages
  const getTotalPages = () => Math.ceil(totalCandidates / ITEMS_PER_PAGE);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= getTotalPages()) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < getTotalPages()) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const totalPages = getTotalPages();

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Auto-refresh countdown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isAutoRefreshEnabled) {
      interval = setInterval(() => {
        let shouldRefresh = false;
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            shouldRefresh = true;
            return REFRESH_INTERVAL; // Reset to 2 minutes
          }
          return prevCountdown - 1;
        });
        if (shouldRefresh && jobId) {
          fetchJobCandidates(true, true); // trigger-matching + screening, same as manual refresh
        }
      }, 1000); // Update every second
    } else {
      // Reset countdown when auto-refresh is disabled
      setCountdown(REFRESH_INTERVAL);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAutoRefreshEnabled, jobId]);

  const fetchJobCandidates = (isRefreshOperation = false, isAutoRefresh = false) => {
    if (!jobId) return;

    const offset = (currentPage - 1) * ITEMS_PER_PAGE;

    console.log(
      `[fetchJobCandidates] Called for job ${jobId}: page ${currentPage}, limit ${ITEMS_PER_PAGE}, offset ${offset}, isRefresh: ${isRefreshOperation}, isFirstTimeLoad: ${isFirstTimeLoad}, isLoading: ${isLoading}`,
    );

    // Set loading state only if not during first-time load initial timer
    // (first-time load sets isLoading to true at the beginning of the 10-second timer)
    if (isRefreshOperation) {
      setIsRefreshing(true);
    } else if (!isLoading) {
      // Only set loading if it's not already set by first-time load
      console.log("[fetchJobCandidates] Setting isLoading to true");
      setIsLoading(true);
    } else {
      console.log("[fetchJobCandidates] isLoading already true, not setting again");
    }

    // Prepare additional parameters for search
    const additionalParams: any = {};
    if (searchTerm && searchTerm.trim()) {
      additionalParams.search = searchTerm.trim();
    }
    additionalParams.status = [0, 1, 7, 9];
    additionalParams.matching_percentage = 0;

    // Add type filter if selected (must be an array)
    if (selectedTypeFilter !== null) {
      additionalParams.type = [selectedTypeFilter];
    }

    // Use trigger-matching API for refresh operations, otherwise use screening/get
    if (isRefreshOperation) {
      // Prepare params for trigger-matching
      const triggerParams: any = {};
      if (isAutoRefresh) {
        triggerParams.refresh = "auto";
      }
      // Add user_id to trigger params
      if (userDetails?.user?.id) {
        triggerParams.user_id = userDetails.user.id;
      }

      // Call trigger-matching first
      jobApi.callTriggerMatching(
        jobId,
        triggerParams,
        (message: string) => {
          console.log("Trigger matching success:", message);
          // After trigger-matching, call screening/get to fetch updated data
          jobApi.callGetScreening(
            jobId,
            ITEMS_PER_PAGE,
            offset,
            additionalParams,
            (message: string, data: any) => {
              handleScreeningResponse(data, isRefreshOperation);
            },
            (errorMessage: string) => {
              handleScreeningError(errorMessage, isRefreshOperation);
            },
          );
        },
        (errorMessage: string) => {
          console.error("Trigger matching failed:", errorMessage);
          // Only show alert if not auto-refresh
          if (!isAutoRefresh) {
            setCooldownAlertMessage(errorMessage || "Failed to trigger matching");
            setShowCooldownAlert(true);
            setIsRefreshing(false);
          } else {
            // For auto-refresh, still call screening/get to update data even if trigger-matching failed
            jobApi.callGetScreening(
              jobId,
              ITEMS_PER_PAGE,
              offset,
              additionalParams,
              (message: string, data: any) => {
                handleScreeningResponse(data, isRefreshOperation);
              },
              (err: string) => {
                handleScreeningError(err, isRefreshOperation);
              },
            );
          }
        },
      );
    } else {
      // Regular fetch using screening/get
      jobApi.callGetScreening(
        jobId,
        ITEMS_PER_PAGE,
        offset,
        additionalParams,
        (message: string, data: any) => {
          handleScreeningResponse(data, isRefreshOperation);
        },
        (errorMessage: string) => {
          handleScreeningError(errorMessage, isRefreshOperation);
        },
      );
    }
  };

  const handleScreeningResponse = (data: any, isRefreshOperation: boolean) => {
    // Clear loading state after API response
    setIsLoading(false);
    setIsRefreshing(false);

    if (data && data.screenings) {
      // Transform API response to match the component interface
      console.log("Fetched screening data:", data);
      const transformedCandidates = data.screenings.map((screening: any) => {
        const jobseekerDetail = screening.jobseeker_details?.[0];
        const basicDetails = jobseekerDetail?.basic_details?.[0];

        // Get candidate name from the basic details structure
        let candidateName = "";
        if (basicDetails?.name && basicDetails.name.trim()) {
          candidateName = basicDetails.name.trim();
        } else if (basicDetails?.first_name && basicDetails?.last_name) {
          candidateName = `${basicDetails.first_name} ${basicDetails.last_name}`.trim();
        } else if (basicDetails?.first_name) {
          candidateName = basicDetails.first_name.trim();
        } else {
          candidateName = "N/A";
        }

        // Get current designation from experience
        const currentDesignation =
          // jobseekerDetail?.experience?.[0]?.designation || basicDetails?.current_designation || "-";
          // screening?.jobseeker_details[0].basic_details[0]?.designation ||
          screening?.jobseeker_details[0].basic_details[0]?.cv_headline ||
          screening?.jobseeker_details[0].basic_details[0]?.experience ||
          "-";

        // Get current location
        const currentLocation = basicDetails?.current_location || "";

        // Get total experience
        const totalExperienceYear = basicDetails?.total_experience_year || "";

        // Get gender
        const genderMap: { [key: number]: string } = {
          1: "Male",
          2: "Female",
        };
        const gender = basicDetails?.gender_id ? genderMap[basicDetails.gender_id] || "-" : "-";

        // Get salary
        const currentSalary = basicDetails?.current_salary || "";
        const currentSalaryCurrency = basicDetails?.current_salary_currency || "";

        let detailsToSet = {
          id: screening.id,
          jobseekerId: screening.jobseeker_id,
          name: candidateName,
          avatar:
            screening.type == 1 || screening.type == 2 ? screening?.document_full_path?.profile_pic || null : null,
          position: basicDetails?.cv_headline || basicDetails?.experience || "-",
          // status: screening.latest_screening_history ? "Screened" : "Not Screened",
          resume_file:
            screening.type == 1 || screening.type == 2
              ? screening?.document_full_path?.system_resume_file || screening?.document_full_path?.resume_file || null
              : null,
          video_file:
            screening.type == 1 || screening.type == 2 ? screening?.document_full_path?.video_file || null : null,
          status: screening.status == CANDIDATE_STATUS.REJECTED ? "Rejected" : "Screened",
          type: screening.type, // Store raw type number
          user_type: screening.user_type || undefined,
          get_new_data: screening.get_new_data,
        };

        // Transform data for ViewProfileModal compatibility
        const candidateDetailsForModal = {
          ...detailsToSet,
          currentDesignation,
          currentLocation,
          totalExperienceYear,
          gender,
          currentSalary,
          currentSalaryCurrency,
          skills: Array.isArray(basicDetails?.key_skills)
            ? basicDetails.key_skills.join(", ")
            : basicDetails?.key_skills || "",
          profileSummary: basicDetails?.profile_summary || "",
          employmentDetails: jobseekerDetail?.experience || [],
          educationDetails: jobseekerDetail?.education || [],
          certificationDetails: jobseekerDetail?.certifications || [],
          courseDetails: jobseekerDetail?.courses || [],
          projectDetails: jobseekerDetail?.projects || [],
          languages: basicDetails?.known_languages || "-",
          hobbyDetails: jobseekerDetail?.hobbies || [],
          referenceDetails: jobseekerDetail?.references || [],
          portfolioDetails: jobseekerDetail?.portfolio || [],
          socialMediaDetails: jobseekerDetail?.social_media || [],
          basicDetails: {
            ...basicDetails,
            resume_file: screening?.document_full_path?.system_resume_file || null,
            video_file: screening?.document_full_path?.video_file || null,
          },
          // Document full path with original resume_file
          document_full_path: screening?.document_full_path || null,
          // Public profile information
          jobseeker_public_profiles: jobseekerDetail?.jobseeker_public_profiles || [],
        };

        console.log("candidateDetailsForModal", candidateDetailsForModal);

        return {
          ...detailsToSet,
          candidateDetails: candidateDetailsForModal, // Store full details for ViewProfileModal
        };
      });

      // Check if this is a refresh operation and if there are any new candidates
      if (isRefreshOperation) {
        const hasNewCandidates = transformedCandidates.some((candidate: Candidate) => candidate.get_new_data === 1);
        if (!hasNewCandidates) {
          setShowNoNewCandidatesAlert(true);
        }
      }

      setCandidates(transformedCandidates);

      if (data.pagination) {
        setTotalCandidates(data.pagination.total || 0);
      }

      // Set extra value available flag
      // If user is on free plan, always set extraValueAvailable to true
      // Otherwise, use the value from API response
      if (isFreePlan && data?.extra_value_available) {
        setExtraValueAvailable(true);
      }
      //  else {
      //   setExtraValueAvailable(data.extra_value_available || false);
      // }
    } else {
      setCandidates([]);
      setTotalCandidates(0);

      // If user is on free plan, set extraValueAvailable to true even when no data
      if (isFreePlan && data?.extra_value_available) {
        setExtraValueAvailable(true);
      }
      //  else {
      //   setExtraValueAvailable(false);
      // }

      // Show message when refresh returns no candidates
      if (isRefreshOperation) {
        setShowNoNewCandidatesAlert(true);
      }
    }
  };

  const handleScreeningError = (errorMessage: string, isRefreshOperation: boolean) => {
    // Error callback
    console.error("Failed to fetch candidates:", errorMessage);
    // If user is on free plan, set extraValueAvailable to true even on error
    if (isFreePlan) {
      setExtraValueAvailable(true);
    } else {
      setExtraValueAvailable(false);
    }
    // Clear loading state after error
    setIsLoading(false);
    setIsRefreshing(false);
    // Clear candidates on error
    setCandidates([]);
    setTotalCandidates(0);
  };

  // Reset state when jobId changes
  useEffect(() => {
    if (jobId) {
      setCurrentPage(1);
      setSearchTerm("");
      setCandidates([]);
      setTotalCandidates(0);
      setIsAutoRefreshEnabled(false);
      setCountdown(120);
      setSelectedCandidate(null);
      setSavedRefreshTime(null);

      // Fetch saved refresh time from API
      if (userDetails?.user?.id) {
        jobApi.callUserJobRefreshGet(
          { user_id: userDetails?.user?.id, job_emp_id: jobId },
          (message: string, data: any) => {
            console.log("User job refresh get success:", data);
            if (data && data.last_refresh_time) {
              // Calculate time elapsed since last refresh
              const lastRefreshTime = new Date(data.last_refresh_time);
              const currentTime = new Date();
              let elapsedSeconds = Math.floor((currentTime.getTime() - lastRefreshTime.getTime()) / 1000);

              if (elapsedSeconds > REFRESH_INTERVAL) {
                console.log(`Elapsed time (${elapsedSeconds}s) exceeds 24 hours, capping at 24 hours`);
                elapsedSeconds = REFRESH_INTERVAL;
              }

              // Calculate remaining time (2 minutes = 120 seconds from last refresh)
              const remainingSeconds = REFRESH_INTERVAL - elapsedSeconds;

              console.log("Last refresh time:", lastRefreshTime);
              console.log("Current time:", currentTime);
              console.log("Elapsed seconds:", elapsedSeconds);
              console.log("Remaining seconds:", remainingSeconds);

              // Only enable auto-refresh if there's time remaining
              if (remainingSeconds > 0) {
                setCountdown(remainingSeconds);
                setIsAutoRefreshEnabled(true);
                setSavedRefreshTime(elapsedSeconds);
              } else {
                // Time has already passed, set countdown for remaining time in cycle
                // trigger-matching will be called when the countdown naturally reaches 0
                console.log("Refresh time has passed, setting countdown for next cycle");
                // setIsAutoRefreshEnabled(true);
                // Calculate remaining time in the current refresh cycle using modulo
                const overflowSeconds = elapsedSeconds % REFRESH_INTERVAL;
                const remainingInCycle = overflowSeconds === 0 ? REFRESH_INTERVAL : REFRESH_INTERVAL - overflowSeconds;
                // setCountdown(remainingInCycle);
                jobApi.callUserJobRefreshSet(
                  { user_id: userDetails?.user?.id, job_emp_id: jobId },
                  (message: string, data: any) => {
                    console.log("User job refresh set success:", data);
                    setIsAutoRefreshEnabled(true);
                    // setCountdown(REFRESH_INTERVAL); // Reset countdown when enabling
                    setCountdown(remainingInCycle);
                    toast.success("Auto-refresh enabled");
                    fetchJobCandidates(true, true); // Immediately trigger-match on activation
                  },
                  (errorMessage: string) => {
                    console.error("Failed to set user job refresh:", errorMessage);
                    toast.error(errorMessage || "Failed to enable auto-refresh");
                  },
                );
              }
            }
          },
          (errorMessage: string) => {
            console.error("Failed to get user job refresh:", errorMessage);
          },
        );
      }

      // Check if this is a first-time load for this job
      const firstTimeLoadFlag = localStorage.getItem(`firstTimeLoad_${jobId}`);
      if (firstTimeLoadFlag === "true") {
        console.log("First-time load detected for job:", jobId);
        setIsFirstTimeLoad(true);
        setFirstTimeLoadComplete(false);
        setIsLoading(true); // Set loading immediately
        // Clear the flag from localStorage
        localStorage.removeItem(`firstTimeLoad_${jobId}`);
      } else {
        console.log("Regular load for job:", jobId);
        setIsFirstTimeLoad(false);
        setFirstTimeLoadComplete(true); // Mark as complete so regular fetch can proceed
      }
    }
  }, [jobId]);

  // Handle first-time load extended loading experience
  useEffect(() => {
    if (isFirstTimeLoad && jobId && !firstTimeLoadComplete) {
      console.log("Starting 10-second timer for first-time load");
      // Show loading for 10 seconds before calling API
      const loadingTime = 10000; // 10 seconds

      // Wait 10 seconds before fetching data
      const fetchTimer = setTimeout(() => {
        console.log("10 seconds elapsed, calling API");
        fetchJobCandidates();
        // After API call, mark first-time load as complete
        setFirstTimeLoadComplete(true);
        setIsFirstTimeLoad(false);
      }, loadingTime);

      return () => {
        clearTimeout(fetchTimer);
      };
    }
  }, [isFirstTimeLoad, jobId, firstTimeLoadComplete]);

  // Fetch candidates when jobId, currentPage, searchTerm, or selectedTypeFilter changes
  useEffect(() => {
    console.log("Regular fetch effect triggered:", { isFirstTimeLoad, firstTimeLoadComplete, jobId });

    // Skip if this is a first-time load (handled by separate effect)
    if (isFirstTimeLoad) {
      console.log("Skipping regular fetch - first-time load in progress");
      return;
    }

    // Only fetch if it's not a first-time load scenario
    if (jobId && firstTimeLoadComplete) {
      console.log("Calling fetchJobCandidates from regular effect");
      fetchJobCandidates();
    }
  }, [jobId, currentPage, searchTerm, selectedTypeFilter, isFirstTimeLoad, firstTimeLoadComplete]);

  const handleViewProfile = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setIsViewProfileOpen(true);
  };

  const handleBulkUploadOpen = () => {
    setIsBulkUploadOpen(true);
  };

  const handleBulkUploadClose = () => {
    setIsBulkUploadOpen(false);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
    // The useEffect will trigger fetchJobCandidates when searchTerm changes
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing search
  };

  const handleAutoRefreshToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isEnabled = e.target.checked;

    if (!jobId || !userDetails?.user?.id) return;

    if (isEnabled) {
      // Call set API when enabling
      jobApi.callUserJobRefreshSet(
        { user_id: userDetails?.user?.id, job_emp_id: jobId },
        (message: string, data: any) => {
          console.log("User job refresh set success:", data);
          setIsAutoRefreshEnabled(true);
          setCountdown(REFRESH_INTERVAL); // Reset countdown when enabling
          toast.success("Auto-refresh enabled");
        },
        (errorMessage: string) => {
          console.error("Failed to set user job refresh:", errorMessage);
          toast.error(errorMessage || "Failed to enable auto-refresh");
        },
      );
    } else {
      // Call remove API when disabling
      jobApi.callUserJobRefreshRemove(
        { user_id: userDetails?.user?.id, job_emp_id: jobId },
        (message: string, data: any) => {
          console.log("User job refresh remove success:", data);
          setIsAutoRefreshEnabled(false);
          toast.success("Auto-refresh disabled");
        },
        (errorMessage: string) => {
          console.error("Failed to remove user job refresh:", errorMessage);
          toast.error(errorMessage || "Failed to disable auto-refresh");
        },
      );
    }
  };

  const formatCountdown = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    console.log("TalentScouting component mounted", selectedCandidate);
  }, [selectedCandidate]);

  return (
    <>
      <div className="talent-scouting-wrapper">
        {/* Header */}
        <div className="talent-scouting-header">
          <div className="header-left-ai-job">
            <div className="job-overview-section">
              <h1 className="job-overview-title">{currentJob?.job_title}</h1>
              <p className="job-overview-subtitle">{t("jobDetails.talentScouting.subtitle")}</p>
            </div>
          </div>
          {/* Only show header-right when there are candidates or search is active */}
          <div className="header-right">
            {(candidates?.length > 0 || searchTerm || isLoading) && (
              <SearchBox
                placeholder={t("jobDetails.talentScouting.searchPlaceholder")}
                onSearch={handleSearch}
                onClear={handleClearSearch}
              />
            )}
            <button className="btn-preview" onClick={handleBulkUploadOpen}>
              <i className="fa fa-upload" style={{ marginRight: "10px" }}></i>
              {t("jobDetails.talentScouting.bulkUploadButton")}
            </button>
          </div>
        </div>

        {/* Candidate Type Filter */}
        <div className="talent-scouting-type-filter" style={{ display: "flex", gap: "8px", padding: "0 0 12px 0" }}>
          {[
            { label: "All", value: null },
            { label: "AI Recommendation", value: 2 },
            { label: "Bulk CV Upload", value: 3 },
            ...(currentJob?.status !== 10 ? [{ label: "Applied Candidates", value: 1 }] : []),
          ].map((filter) => (
            <button
              key={String(filter.value)}
              className={`filter-tab-btn ${selectedTypeFilter === filter.value ? "active" : ""}`}
              style={{
                padding: "6px 14px",
                borderRadius: "20px",
                border: selectedTypeFilter === filter.value ? "1px solid #a5c5e5" : "1px solid #d0d5dd",
                background: selectedTypeFilter === filter.value ? "#a5c5e5" : "#fff",
                color: selectedTypeFilter === filter.value ? "#fff" : "#344054",
                fontSize: "13px",
                cursor: "pointer",
                fontWeight: selectedTypeFilter === filter.value ? 600 : 400,
              }}
              onClick={() => {
                setSelectedTypeFilter(filter.value);
                setCurrentPage(1);
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Show ProgressIndicator when no candidates and no search */}
        {/* {!isLoading && candidates?.length === 0 && !searchTerm ? (
        <ProgressIndicator stage="talent-scouting" />
      ) : ( */}
        <div className="talent-scouting-inner-wrapper">
          {/* Position Filter Section */}
          <div className="filter-section">
            <div className="position-info">
              <h2 className="position-title">{currentJob?.job_title}</h2>
              <p className="candidate-count-text">
                <span className="count-number">{totalCandidates}</span>{" "}
                {searchTerm ? t("jobDetails.talentScouting.matching") : t("jobDetails.talentScouting.potential")}{" "}
                {t("jobDetails.talentScouting.candidatesFound")}{" "}
                {searchTerm && `${t("jobDetails.talentScouting.for")} "${searchTerm}"`}
              </p>
            </div>
            {isRefreshing && (
              <div className="refresh-loader-overlay">
                <div className="loading-spinner"></div>
                <span className="refresh-text">{t("jobDetails.talentScouting.refreshing")}</span>
              </div>
            )}
            <div className="filter-actions">
              {isAutoRefreshEnabled && (
                <div className="countdown-display">
                  <span className="countdown-text">
                    {t("jobDetails.talentScouting.nextRefresh")}: {formatCountdown(countdown)}
                  </span>
                </div>
              )}
              <button
                className="refresh-btn"
                onClick={() => {
                  fetchJobCandidates(true); // Pass true to indicate it's a refresh operation
                }}
                disabled={isRefreshing}
              >
                <i className="fa fa-refresh"></i>
                {t("jobDetails.talentScouting.refresh")}
              </button>
              <div className="auto-refresh-toggle">
                <span className="auto-refresh-text" style={{ fontSize: "14px" }}>
                  {t("jobDetails.talentScouting.autoRefresh")}
                </span>
                <button
                  className={`toggle-switch ${isAutoRefreshEnabled ? "active" : ""}`}
                  disabled={isTogglingRefresh}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("Toggle clicked", {
                      jobId,
                      userId: userDetails?.user?.id,
                      isAutoRefreshEnabled,
                      isTogglingRefresh,
                      event: e,
                    });

                    if (!jobId || !userDetails?.user?.id) {
                      console.error("Missing jobId or userId", { jobId, userId: userDetails?.user?.id });
                      toast.error("Unable to toggle auto-refresh. Missing required information.");
                      return;
                    }

                    if (isTogglingRefresh) {
                      console.log("Already toggling, ignoring click");
                      return;
                    }

                    setIsTogglingRefresh(true);

                    if (!isAutoRefreshEnabled) {
                      // Call set API when enabling
                      console.log("Calling set API");
                      jobApi.callUserJobRefreshSet(
                        { user_id: userDetails?.user?.id, job_emp_id: jobId },
                        (message: string, data: any) => {
                          console.log("User job refresh set success:", data);
                          setIsAutoRefreshEnabled(true);
                          setCountdown(REFRESH_INTERVAL); // Reset countdown when enabling
                          setIsTogglingRefresh(false);
                          toast.success("Auto-refresh enabled");
                          fetchJobCandidates(true, true); // Immediately trigger-match on activation
                        },
                        (errorMessage: string) => {
                          console.error("Failed to set user job refresh:", errorMessage);
                          setIsTogglingRefresh(false);
                          toast.error(errorMessage || "Failed to enable auto-refresh");
                        },
                      );
                    } else {
                      // Call remove API when disabling
                      console.log("Calling remove API");
                      jobApi.callUserJobRefreshRemove(
                        { user_id: userDetails?.user?.id, job_emp_id: jobId },
                        (message: string, data: any) => {
                          console.log("User job refresh remove success:", data);
                          setIsAutoRefreshEnabled(false);
                          setIsTogglingRefresh(false);
                          toast.success("Auto-refresh disabled");
                        },
                        (errorMessage: string) => {
                          console.error("Failed to remove user job refresh:", errorMessage);
                          setIsTogglingRefresh(false);
                          toast.error(errorMessage || "Failed to disable auto-refresh");
                        },
                      );
                    }
                  }}
                  style={{
                    width: "44px",
                    height: "24px",
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: isAutoRefreshEnabled ? "#3b82f6" : "#e5e7eb",
                    position: "relative",
                    cursor: isTogglingRefresh ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s",
                    marginLeft: "8px",
                    opacity: isTogglingRefresh ? 0.6 : 1,
                    pointerEvents: isTogglingRefresh ? "none" : "auto",
                    // zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      position: "absolute",
                      top: "2px",
                      left: isAutoRefreshEnabled ? "22px" : "2px",
                      transition: "left 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      pointerEvents: "none",
                    }}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Candidates Table */}
          <div className="table-wrapper">
            {!isLoading && candidates?.length === 0 && !searchTerm ? (
              <ProgressIndicator stage="talent-scouting" />
            ) : (
              // <></>
              <table className="candidates-table">
                <thead>
                  <tr>
                    <th className="text-center">{t("jobDetails.talentScouting.table.slNo")}</th>
                    <th>{t("jobDetails.talentScouting.table.candidateName")}</th>
                    <th>{t("jobDetails.talentScouting.table.currentPosition")}</th>
                    <th className="text-center">{t("jobDetails.talentScouting.table.details")}</th>
                    <th className="text-center">{t("jobDetails.talentScouting.table.type")}</th>
                    <th className="text-center">{t("jobDetails.talentScouting.table.screening")}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "40px" }}>
                        <div className="loading-container">
                          <div className="loading-spinner"></div>
                        </div>
                        <p style={{ marginTop: "20px", color: "#666", fontSize: "14px", lineHeight: "1.6" }}>
                          {t("jobDetails.talentScouting.loadingMessage")}
                        </p>
                      </td>
                    </tr>
                  ) : candidates?.length > 0 ? (
                    candidates
                      .filter((c) => selectedTypeFilter === null || c.type === selectedTypeFilter)
                      .map((candidate, index) => (
                        <tr key={candidate.id}>
                          <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                          <td>
                            <div className="candidate-cell">
                              <div className="candidate-avatar">
                                <img
                                  src={candidate.avatar || "/images/user-avatar.jpg"}
                                  alt="User Avatar"
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                              <span className="candidate-name">
                                {candidate.name}
                                {candidate.get_new_data === 1 && <span className="new-badge">New</span>}
                              </span>
                            </div>
                          </td>
                          <td className="position-cell">{candidate?.candidateDetails?.currentDesignation}</td>
                          <td>
                            <button className="view-profile-btn" onClick={() => handleViewProfile(candidate)}>
                              {t("jobDetails.talentScouting.viewProfile")}
                            </button>
                          </td>
                          <td className="position-cell">
                            {candidate.type === 1
                              ? t("jobDetails.talentScouting.types.applied")
                              : candidate.type === 2
                                ? t("jobDetails.talentScouting.types.recommended")
                                : candidate.type === 3
                                  ? t("jobDetails.talentScouting.types.bulkUpload")
                                  : ""}
                          </td>
                          <td>
                            <span className={(candidate.status == "Rejected" ? "rejected" : "screening") + "-badge"}>
                              {candidate.status}
                            </span>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "20px" }}>
                        {searchTerm
                          ? `${t("jobDetails.talentScouting.noCandidatesMatching")} "${searchTerm}"`
                          : t("jobDetails.talentScouting.noCandidates")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && candidates.length > 0 && getTotalPages() > 1 && (
            <div className="pagination">
              <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
                {t("jobDetails.talentScouting.prev")}
              </button>
              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`}>...</span>
                  ) : (
                    <button
                      key={page}
                      className={`page-num ${currentPage === page ? "active" : ""}`}
                      onClick={() => handlePageChange(page as number)}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>
              <button className="page-btn" onClick={handleNextPage} disabled={currentPage === getTotalPages()}>
                {t("jobDetails.talentScouting.next")}
              </button>
            </div>
          )}
        </div>
        {/* )} */}

        {/* Bulk Resume Upload Modal */}
        <BulkResumeUploadModal isOpen={isBulkUploadOpen} onClose={handleBulkUploadClose} jobId={jobId} />

        {/* View Profile Modal */}
        {isViewProfileOpen && jobId && selectedCandidate?.jobseekerId && (
          <ViewProfileModal
            isOpen={isViewProfileOpen}
            onClose={() => setIsViewProfileOpen(false)}
            jobId={jobId}
            jobseekerId={selectedCandidate?.jobseekerId}
            candidateType={selectedCandidate?.type}
            user_type={selectedCandidate?.user_type}
          />
        )}

        {/* Upgrade Plan Modal */}
        <UpgradePlanModal isVisible={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      </div>
      {/* Upgrade Plan Message */}
      {extraValueAvailable && (
        <div
          className="upgrade-plan-message"
          style={{
            textAlign: "center",
            padding: "15px 20px",
            marginTop: "20px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            color: "#856404",
          }}
        >
          <i className="fa fa-info-circle" style={{ marginRight: "8px" }}></i>
          <strong>{t("jobDetails.talentScouting.upgradeMessage")}</strong>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowUpgradeModal(true);
            }}
            style={{
              marginLeft: "10px",
              color: "#0066cc",
              textDecoration: "underline",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            {t("jobDetails.talentScouting.upgradePlan")}
          </a>
        </div>
      )}

      {/* No New Candidates Alert */}
      {showNoNewCandidatesAlert && (
        <SweetAlert
          info
          confirmBtnText="OK"
          confirmBtnBsStyle="preview"
          title={t("jobDetails.talentScouting.noNewCandidates")}
          onConfirm={() => setShowNoNewCandidatesAlert(false)}
        />
      )}

      {/* Cooldown Period Alert */}
      {showCooldownAlert && (
        <SweetAlert
          info
          confirmBtnText="OK"
          confirmBtnBsStyle="preview"
          title="Matching Cooldown"
          onConfirm={() => setShowCooldownAlert(false)}
        >
          {cooldownAlertMessage}
        </SweetAlert>
      )}
    </>
  );
}
